import { supabase } from '../lib/supabase';

export const bucketConfigService = {
  // Get all bucket configurations for current user
  async getAll() {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('bucket_configurations')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(config => ({
      id: config?.id,
      userId: config?.user_id,
      bucketName: config?.bucket_name,
      bucketRegion: config?.bucket_region,
      accessKeyId: config?.access_key_id,
      secretAccessKey: config?.secret_access_key,
      publicUrl: config?.public_url,
      bucketType: config?.bucket_type,
      isDefault: config?.is_default,
      lastAccessedAt: config?.last_accessed_at,
      createdAt: config?.created_at,
      updatedAt: config?.updated_at
    })) || [];
  },

  // Get single bucket configuration
  async getById(bucketId) {
    const { data, error } = await supabase?.from('bucket_configurations')?.select('*')?.eq('id', bucketId)?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      bucketName: data?.bucket_name,
      bucketRegion: data?.bucket_region,
      accessKeyId: data?.access_key_id,
      secretAccessKey: data?.secret_access_key,
      publicUrl: data?.public_url,
      bucketType: data?.bucket_type,
      isDefault: data?.is_default,
      lastAccessedAt: data?.last_accessed_at,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Create new bucket configuration
  async create(bucketData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('bucket_configurations')?.insert({
        user_id: user?.id,
        bucket_name: bucketData?.bucketName,
        bucket_region: bucketData?.bucketRegion,
        access_key_id: bucketData?.accessKeyId,
        secret_access_key: bucketData?.secretAccessKey,
        public_url: bucketData?.publicUrl,
        bucket_type: bucketData?.bucketType || 'personal',
        is_default: bucketData?.isDefault || false
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      bucketName: data?.bucket_name,
      bucketRegion: data?.bucket_region,
      accessKeyId: data?.access_key_id,
      secretAccessKey: data?.secret_access_key,
      publicUrl: data?.public_url,
      bucketType: data?.bucket_type,
      isDefault: data?.is_default,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update bucket configuration
  async update(bucketId, updates) {
    const updateData = {};
    if (updates?.bucketName) updateData.bucket_name = updates?.bucketName;
    if (updates?.bucketRegion) updateData.bucket_region = updates?.bucketRegion;
    if (updates?.accessKeyId) updateData.access_key_id = updates?.accessKeyId;
    if (updates?.secretAccessKey) updateData.secret_access_key = updates?.secretAccessKey;
    if (updates?.publicUrl) updateData.public_url = updates?.publicUrl;
    if (updates?.bucketType) updateData.bucket_type = updates?.bucketType;
    if (updates?.isDefault !== undefined) updateData.is_default = updates?.isDefault;

    const { data, error } = await supabase?.from('bucket_configurations')?.update(updateData)?.eq('id', bucketId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      bucketName: data?.bucket_name,
      bucketRegion: data?.bucket_region,
      accessKeyId: data?.access_key_id,
      secretAccessKey: data?.secret_access_key,
      publicUrl: data?.public_url,
      bucketType: data?.bucket_type,
      isDefault: data?.is_default,
      updatedAt: data?.updated_at
    };
  },

  // Delete bucket configuration
  async delete(bucketId) {
    const { error } = await supabase?.from('bucket_configurations')?.delete()?.eq('id', bucketId);

    if (error) throw error;
    return { success: true };
  },

  // Update last accessed timestamp
  async updateLastAccessed(bucketId) {
    const { error } = await supabase?.from('bucket_configurations')?.update({ last_accessed_at: new Date()?.toISOString() })?.eq('id', bucketId);

    if (error) throw error;
    return { success: true };
  },

  // Subscribe to real-time changes for bucket configurations
  subscribeToChanges(userId, callback) {
    const channel = supabase?.channel('bucket_configurations_changes')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bucket_configurations',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          // Convert snake_case to camelCase for consistency
          const convertedPayload = {
            eventType: payload?.eventType,
            new: payload?.new ? {
              id: payload?.new?.id,
              userId: payload?.new?.user_id,
              bucketName: payload?.new?.bucket_name,
              bucketRegion: payload?.new?.bucket_region,
              accessKeyId: payload?.new?.access_key_id,
              secretAccessKey: payload?.new?.secret_access_key,
              publicUrl: payload?.new?.public_url,
              bucketType: payload?.new?.bucket_type,
              isDefault: payload?.new?.is_default,
              lastAccessedAt: payload?.new?.last_accessed_at,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            } : null,
            old: payload?.old ? {
              id: payload?.old?.id
            } : null
          };
          callback(convertedPayload);
        }
      )?.subscribe();

    return channel;
  },

  // Unsubscribe from real-time changes
  unsubscribeFromChanges(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};